import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { AdminHeader } from "./AdminLayout";
import API_ROUTES from "../../hooks/apiRoutes";
import { apiGet, apiSend, useAdminState, Erro, Toast } from "../../hooks/useAdminApi";
import { uploadToBunny } from "../../lib/upload";

/**
 * CONTEÚDOS — o acervo: criar, editar e a CURADORIA DE FAIXA ETÁRIA.
 *
 * Duas coisas convivem nesta tela, e é de propósito:
 *
 * 1. CRIAR/EDITAR conteúdo (o modal). Era o ÚNICO motivo que ainda obrigava o time a
 *    abrir o painel antigo — com isto aqui, o antigo deixa de ser necessário.
 *
 * 2. CURADORIA EM LOTE de faixa etária. A 1ª pergunta do funil é a idade da criança, mas
 *    isso não filtra nada enquanto os conteúdos não tiverem faixa. São centenas, e um por
 *    um levaria dias: daí a aplicação em LOTE.
 *
 * ⚠️ Faixa em branco = "serve pra qualquer idade". Conteúdo sem faixa CONTINUA VISÍVEL.
 * A faixa RECOMENDA, não bloqueia — bloquear com o campo vazio deixaria a criança com a
 * tela vazia no dia 1.
 *
 * A API fala em snake_case com prefixo (`content_title`) e o front em camelCase. A tradução
 * fica nas bordas: `paraFormulario` na leitura, `montarCorpo` na escrita.
 */

/**
 * ⚠️ A LISTA (`GET /contents`) É A FONTE DA EDIÇÃO — NÃO `GET /contents/:id`.
 *
 * O endpoint de UM conteúdo aplica a regra de paywall em cima de QUEM PEDIU: se o conteúdo
 * for `premium` e o admin logado só tiver `free` (é o caso — o JWT do admin traz
 * subscriptions: ["free"]), ele responde `content_url: ""` e `is_blocked: true`.
 * Carregar o formulário dali e salvar APAGARIA o vídeo de todo conteúdo premium, em
 * silêncio. A lista de admin não passa por essa regra e devolve os campos como estão.
 * Verificado na API local: /contents/:id premium -> url ""; /contents -> url real.
 */
const paraFormulario = (c) => ({
  id: c.content_id,
  title: c.content_title || "",
  subtitle: c.content_subtitle || "",
  description: c.content_description || "",
  contentType: c.content_type || "video",
  thumbnailUrl: c.content_thumbnail_url || "",
  contentUrl: c.content_url || "",
  templateId: c.content_template_id || "",
  access: c.content_access || [],
  additionalInfo: c.content_additional_info || "",
});

const formVazio = () => ({
  id: null,
  title: "",
  subtitle: "",
  description: "",
  contentType: "video",
  thumbnailUrl: "",
  contentUrl: "",
  templateId: "",
  access: [],
  additionalInfo: "",
});

/**
 * Criar exige TODOS os campos presentes (o zod do backend não os aceita ausentes, embora
 * aceite string vazia). Editar aceita parcial — mas aqui mandamos tudo, porque a lista nos
 * deu os valores REAIS e atuais. Diferente de Séries, `content_additional_info` VEM no GET,
 * então não há o risco de sobrescrever com "" o que já estava salvo.
 */
const montarCorpo = (f) => ({
  content_title: f.title.trim(),
  content_subtitle: f.subtitle.trim(),
  content_description: f.description.trim(),
  content_type: f.contentType,
  content_thumbnail_url: f.thumbnailUrl.trim(),
  content_url: f.contentUrl.trim(),
  content_template_id: f.templateId,
  content_access: f.access,
  content_additional_info: f.additionalInfo,
});

export default function Contents() {
  const { tema, setTema } = useOutletContext();
  const { erro, toast, rodar, limparErro } = useAdminState();

  const [dados, setDados] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState("todos"); // todos | sem_faixa | com_faixa
  const [sel, setSel] = useState(new Set());
  const [loteMin, setLoteMin] = useState("");
  const [loteMax, setLoteMax] = useState("");

  // Para criar/editar: os templates (a página de venda) e o acervo COMPLETO indexado por id
  // — é dele que o formulário de edição se abastece (ver a nota em `paraFormulario`).
  const [templates, setTemplates] = useState([]);
  const [acervo, setAcervo] = useState({});
  const [form, setForm] = useState(null);
  const [erroForm, setErroForm] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const buscarCuracao = useCallback(
    async (b = busca, f = filtro) =>
      setDados(
        await apiGet(API_ROUTES.ADMIN.curation({ search: b, filtro: f, size: 100 }))
      ),
    [busca, filtro]
  );

  const buscarAuxiliares = useCallback(async () => {
    const [listaTemplates, listaConteudos] = await Promise.all([
      apiGet(API_ROUTES.TEMPLATES.list()),
      apiGet(API_ROUTES.CONTENTS.list()),
    ]);
    setTemplates(listaTemplates.templates || []);
    setAcervo(
      Object.fromEntries((listaConteudos.contents || []).map((c) => [c.content_id, c]))
    );
  }, []);

  const carregar = useCallback(
    (b = busca, f = filtro) => rodar(() => buscarCuracao(b, f)),
    [buscarCuracao, rodar, busca, filtro]
  );

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtro]);

  // Templates e acervo não dependem de busca/filtro: uma vez só, na entrada.
  useEffect(() => {
    rodar(buscarAuxiliares);
  }, [rodar, buscarAuxiliares]);

  // Busca só dispara depois que a pessoa para de digitar.
  useEffect(() => {
    const t = setTimeout(() => carregar(busca, filtro), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca]);

  const alternar = (id) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const todosMarcados =
    dados?.itens.length > 0 && dados.itens.every((i) => sel.has(i.id));

  const alternarTodos = () =>
    setSel((s) =>
      todosMarcados ? new Set() : new Set(dados.itens.map((i) => i.id))
    );

  const aplicarLote = (limpar = false) =>
    rodar(async () => {
      const r = await apiSend(API_ROUTES.ADMIN.curationBulk(), "POST", {
        ids: [...sel],
        ageMin: limpar ? null : loteMin === "" ? null : Number(loteMin),
        ageMax: limpar ? null : loteMax === "" ? null : Number(loteMax),
      });
      setSel(new Set());
      setLoteMin("");
      setLoteMax("");
      await buscarCuracao();
      return r;
    }, limpar
      ? "Faixa removida — voltam a servir para todas as idades."
      : `Faixa aplicada a ${sel.size} ${sel.size === 1 ? "conteúdo" : "conteúdos"}.`);

  const salvarLinha = (id, min, max) =>
    rodar(async () => {
      await apiSend(API_ROUTES.ADMIN.curationOne(id), "PATCH", {
        ageMin: min === "" ? null : Number(min),
        ageMax: max === "" ? null : Number(max),
      });
      await buscarCuracao();
    });

  /** As tags de acesso não têm cadastro próprio: as que existem são as que o acervo já usa. */
  const tagsAcesso = useMemo(() => {
    const tags = new Set(["free", "premium"]); // as duas que o app entende hoje
    Object.values(acervo).forEach((c) => (c.content_access || []).forEach((a) => tags.add(a)));
    return [...tags].sort();
  }, [acervo]);

  const abrirNovo = () => {
    setErroForm(null);
    setForm(formVazio());
  };

  const abrirEdicao = (id) => {
    const bruto = acervo[id];
    if (!bruto) {
      // A linha veio da curadoria, mas o acervo ainda não chegou (ou falhou). Editar sem os
      // valores atuais salvaria campos em branco por cima dos bons — melhor não abrir.
      rodar(async () => {
        await buscarAuxiliares();
        throw new Error(
          "Não consegui carregar os dados completos deste conteúdo. A lista foi recarregada — tente editar de novo."
        );
      });
      return;
    }
    setErroForm(null);
    setForm(paraFormulario(bruto));
  };

  /**
   * Excluir. O backend faz SOFT delete e em CASCATA: além do conteúdo, ele marca como
   * apagados os `chapters` (as faixas da home) e os `episodes` (as séries) que apontam
   * para ele — ver ContentController.deleteContent. Ou seja: não quebra série nem home,
   * o item simplesmente some dos dois. Mas some SEM AVISO — por isso o modal avisa antes.
   */
  const excluir = async (f) => {
    setErroForm(null);
    setSalvando(true);
    try {
      await apiSend(API_ROUTES.CONTENTS.delete(f.id), "DELETE");
    } catch (e) {
      setErroForm(e.message);
      setSalvando(false);
      return;
    }
    setSalvando(false);
    setForm(null);
    rodar(async () => {
      await Promise.all([buscarCuracao(), buscarAuxiliares()]);
    }, "Conteúdo excluído.");
  };

  const salvar = async (f) => {
    setErroForm(null);
    setSalvando(true);
    try {
      const corpo = montarCorpo(f);
      if (f.id) await apiSend(API_ROUTES.CONTENTS.update(f.id), "PUT", corpo);
      else await apiSend(API_ROUTES.CONTENTS.create(), "POST", corpo);
    } catch (e) {
      // O erro fica DENTRO do modal de propósito: mostrá-lo no corpo da página o esconderia
      // atrás do próprio modal, e a pessoa veria o botão "não funcionar" sem motivo aparente.
      setErroForm(e.message);
      setSalvando(false);
      return;
    }
    setSalvando(false);
    setForm(null);
    rodar(
      async () => {
        await Promise.all([buscarCuracao(), buscarAuxiliares()]);
      },
      f.id ? "Conteúdo atualizado." : "Conteúdo criado."
    );
  };

  const p = dados?.progresso;

  return (
    <>
      <AdminHeader
        tema={tema}
        setTema={setTema}
        titulo="Conteúdos"
        subtitulo={
          <>
            O acervo inteiro: crie, edite e defina a faixa etária. Conteúdo sem faixa continua
            visível para todos — a faixa serve para <strong>recomendar</strong> o conteúdo certo
            para cada idade, não para bloquear.
          </>
        }
      />

      <div className="adm__body">
        <Erro erro={erro} onFechar={limparErro} />

        {/* ---------- o progresso da curadoria ---------- */}
        {p && (
          <div className="adm__card" style={{ padding: "18px 22px", marginBottom: 18 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 10,
              }}
            >
              <strong style={{ fontSize: 14 }}>Curadoria de faixa etária</strong>
              <span style={{ fontSize: 13, color: "var(--text2)" }}>
                <strong style={{ fontSize: 21, color: "var(--text)" }}>{p.comFaixa}</strong>{" "}
                de {p.total}{" "}
                <strong style={{ color: "var(--red)", marginLeft: 4 }}>{p.percentual}%</strong>
              </span>
            </div>
            <div
              style={{
                height: 7,
                borderRadius: 999,
                background: "var(--elev)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${p.percentual}%`,
                  background: "var(--red)",
                  borderRadius: 999,
                  transition: "width .3s",
                }}
              />
            </div>
            <p style={{ margin: "9px 0 0", fontSize: 12.5, color: "var(--text2)" }}>
              <strong style={{ color: "var(--orange)" }}>{p.faltam}</strong> conteúdos ainda
              sem faixa definida
            </p>
          </div>
        )}

        {/* ---------- ferramentas ---------- */}
        <div style={{ display: "flex", gap: 10, marginBottom: 14, alignItems: "center" }}>
          <input
            className="adm__input"
            placeholder="Buscar por título…"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ flex: 1 }}
          />
          <div style={{ display: "flex", gap: 4 }}>
            {[
              ["todos", "Todos"],
              ["sem_faixa", "Sem faixa definida"],
              ["com_faixa", "Com faixa"],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setFiltro(k)}
                className={filtro === k ? "adm__btn" : "adm__btnGhost"}
              >
                {l}
              </button>
            ))}
          </div>
          <button className="adm__btn" onClick={abrirNovo}>
            + Novo conteúdo
          </button>
        </div>

        {/* ---------- tabela ---------- */}
        <div className="adm__card" style={{ overflow: "hidden" }}>
          <table className="adm__tabela">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    checked={todosMarcados}
                    onChange={alternarTodos}
                    style={{ accentColor: "var(--red)", cursor: "pointer" }}
                  />
                </th>
                <th>Título</th>
                <th style={{ width: 120 }}>Tipo</th>
                <th style={{ width: 190 }}>Acesso</th>
                <th style={{ width: 210 }}>Faixa etária</th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {dados?.itens.map((c) => (
                <Linha
                  key={c.id}
                  c={c}
                  marcado={sel.has(c.id)}
                  onMarcar={() => alternar(c.id)}
                  onSalvar={salvarLinha}
                  onEditar={() => abrirEdicao(c.id)}
                />
              ))}
            </tbody>
          </table>
          {dados && dados.itens.length === 0 && (
            <p className="adm__vazio">
              {filtro === "com_faixa"
                ? "Nenhum conteúdo tem faixa definida ainda. Comece selecionando alguns em “Sem faixa definida”."
                : "Nenhum conteúdo encontrado."}
            </p>
          )}
        </div>
      </div>

      {/* ---------- a barra de ação em lote: transforma dias em minutos ---------- */}
      {sel.size > 0 && (
        <div
          style={{
            position: "fixed",
            left: 250,
            right: 0,
            bottom: 0,
            background: "var(--elev)",
            borderTop: "1px solid var(--border)",
            padding: "14px 34px",
            display: "flex",
            alignItems: "center",
            gap: 16,
            zIndex: 100,
            boxShadow: "0 -10px 30px var(--shadow)",
          }}
        >
          <strong style={{ fontSize: 13.5 }}>
            {sel.size} {sel.size === 1 ? "selecionado" : "selecionados"}
          </strong>

          <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13 }}>
            <span style={{ color: "var(--text2)" }}>Aplicar faixa: de</span>
            <input
              className="adm__input"
              type="number"
              min="0"
              max="18"
              value={loteMin}
              onChange={(e) => setLoteMin(e.target.value)}
              style={{ width: 58, textAlign: "center", padding: "7px 6px" }}
            />
            <span style={{ color: "var(--text2)" }}>a</span>
            <input
              className="adm__input"
              type="number"
              min="0"
              max="18"
              value={loteMax}
              onChange={(e) => setLoteMax(e.target.value)}
              style={{ width: 58, textAlign: "center", padding: "7px 6px" }}
            />
            <span style={{ color: "var(--text2)" }}>anos</span>
          </div>

          <div style={{ marginLeft: "auto", display: "flex", gap: 9 }}>
            <button className="adm__btnGhost" onClick={() => setSel(new Set())}>
              Limpar seleção
            </button>
            <button
              className="adm__btnGhost"
              onClick={() => aplicarLote(true)}
              title="Volta a servir para todas as idades"
            >
              Remover faixa
            </button>
            <button
              className="adm__btn"
              disabled={loteMin === "" && loteMax === ""}
              onClick={() => aplicarLote(false)}
            >
              Aplicar faixa
            </button>
          </div>
        </div>
      )}

      {form && (
        <ModalConteudo
          form={form}
          templates={templates}
          tagsAcesso={tagsAcesso}
          erro={erroForm}
          onLimparErro={() => setErroForm(null)}
          salvando={salvando}
          onFechar={() => setForm(null)}
          onSalvar={salvar}
          onExcluir={excluir}
        />
      )}

      <Toast toast={toast} />
    </>
  );
}

/** Uma linha, com a faixa editável direto (sem abrir modal). */
function Linha({ c, marcado, onMarcar, onSalvar, onEditar }) {
  const [min, setMin] = useState(c.ageMin ?? "");
  const [max, setMax] = useState(c.ageMax ?? "");

  useEffect(() => {
    setMin(c.ageMin ?? "");
    setMax(c.ageMax ?? "");
  }, [c.ageMin, c.ageMax]);

  const semFaixa = c.ageMin === null && c.ageMax === null;
  const mudou = String(min) !== String(c.ageMin ?? "") || String(max) !== String(c.ageMax ?? "");

  return (
    <tr>
      <td>
        <input
          type="checkbox"
          checked={marcado}
          onChange={onMarcar}
          style={{ accentColor: "var(--red)", cursor: "pointer" }}
        />
      </td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ fontWeight: 600 }}>{c.title}</span>
          {semFaixa && <span className="adm__chip adm__chip--orange">sem faixa</span>}
        </div>
      </td>
      <td>
        <span className="adm__chip">{c.type === "document" ? "Documento" : "Vídeo"}</span>
      </td>
      <td>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {(c.access || []).map((a) => (
            <span key={a} className="adm__chip adm__chip--red">
              {a}
            </span>
          ))}
        </div>
      </td>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5 }}>
          <span style={{ color: "var(--text3)" }}>de</span>
          <input
            className="adm__input"
            type="number"
            min="0"
            max="18"
            value={min}
            onChange={(e) => setMin(e.target.value)}
            style={{ width: 48, textAlign: "center", padding: "6px 4px" }}
          />
          <span style={{ color: "var(--text3)" }}>a</span>
          <input
            className="adm__input"
            type="number"
            min="0"
            max="18"
            value={max}
            onChange={(e) => setMax(e.target.value)}
            style={{ width: 48, textAlign: "center", padding: "6px 4px" }}
          />
          <span style={{ color: "var(--text3)" }}>anos</span>
          {mudou && (
            <button
              className="adm__btn"
              style={{ padding: "5px 10px", fontSize: 11.5 }}
              onClick={() => onSalvar(c.id, min, max)}
            >
              Salvar
            </button>
          )}
        </div>
      </td>
      <td>
        <button className="adm__btnGhost" onClick={onEditar} style={{ padding: "5px 11px" }}>
          Editar
        </button>
      </td>
    </tr>
  );
}

/** Barrinha de progresso do upload. O vídeo leva minutos — sem ela, a pessoa acha que travou. */
function Progresso({ valor }) {
  return (
    <div
      style={{
        height: 6,
        borderRadius: 999,
        background: "var(--card)",
        overflow: "hidden",
        marginTop: 9,
      }}
    >
      <div
        style={{
          height: "100%",
          width: `${valor}%`,
          background: "var(--red)",
          borderRadius: 999,
          transition: "width .2s",
        }}
      />
    </div>
  );
}

/** A caixa tracejada de upload — mesmo desenho para vídeo e capa. */
function CaixaUpload({ children }) {
  return (
    <div
      style={{
        border: "1px dashed var(--border)",
        borderRadius: 10,
        padding: 14,
        background: "var(--elev)",
      }}
    >
      {children}
    </div>
  );
}

function ModalConteudo({
  form,
  templates,
  tagsAcesso,
  erro,
  onLimparErro,
  salvando,
  onFechar,
  onSalvar,
  onExcluir,
}) {
  const [f, setF] = useState(form);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [video, setVideo] = useState({ enviando: false, progresso: 0, erro: "", ok: false });
  const [capa, setCapa] = useState({ enviando: false, progresso: 0, erro: "", ok: false });
  const [capaVeioDoVideo, setCapaVeioDoVideo] = useState(false);
  const [novaTag, setNovaTag] = useState("");

  const inputVideo = useRef(null);
  const inputCapa = useRef(null);

  /**
   * Espelho da capa atual. O upload do vídeo dura minutos, e a pessoa pode colar uma URL de
   * capa NO MEIO dele: quando ele termina, o `f` capturado no closure já está velho. O ref
   * sempre tem o valor de agora — é ele que decide se a capa do Bunny pode ou não entrar.
   */
  const capaAtual = useRef(f.thumbnailUrl);
  useEffect(() => {
    capaAtual.current = f.thumbnailUrl;
  }, [f.thumbnailUrl]);

  const editando = Boolean(f.id);
  const ehVideo = f.contentType === "video";
  const set = (k, v) => setF((atual) => ({ ...atual, [k]: v }));

  const aoEscolherVideo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite reescolher o MESMO arquivo depois de um erro
    if (!file) return;
    setVideo({ enviando: true, progresso: 0, erro: "", ok: false });
    try {
      const res = await uploadToBunny(
        API_ROUTES.UPLOADS.video(),
        file,
        { title: f.title.trim() || file.name },
        (p) => setVideo((s) => ({ ...s, progresso: p }))
      );
      // A capa que o Bunny gera é um FRAME do vídeo: serve de padrão, mas nunca por cima de
      // uma escolha da pessoa.
      const usouCapaDoBunny = !capaAtual.current && Boolean(res.thumbnailUrl);
      setF((atual) => ({
        ...atual,
        contentUrl: res.embedUrl || atual.contentUrl,
        thumbnailUrl: atual.thumbnailUrl || res.thumbnailUrl || "",
      }));
      if (usouCapaDoBunny) setCapaVeioDoVideo(true);
      setVideo({ enviando: false, progresso: 100, erro: "", ok: true });
    } catch (err) {
      setVideo({ enviando: false, progresso: 0, erro: err.message, ok: false });
    }
  };

  const aoEscolherCapa = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setCapa({ enviando: true, progresso: 0, erro: "", ok: false });
    try {
      const res = await uploadToBunny(
        API_ROUTES.UPLOADS.thumbnail(),
        file,
        {},
        (p) => setCapa((s) => ({ ...s, progresso: p }))
      );
      set("thumbnailUrl", res.url);
      setCapaVeioDoVideo(false);
      setCapa({ enviando: false, progresso: 100, erro: "", ok: true });
    } catch (err) {
      setCapa({ enviando: false, progresso: 0, erro: err.message, ok: false });
    }
  };

  const alternarAcesso = (t) =>
    set("access", f.access.includes(t) ? f.access.filter((x) => x !== t) : [...f.access, t]);

  const adicionarTag = () => {
    const t = novaTag.trim().toLowerCase();
    if (!t || f.access.includes(t)) return setNovaTag("");
    set("access", [...f.access, t]);
    setNovaTag("");
  };

  /**
   * Validação HONESTA: em vez de um botão cinza sem explicação, a lista diz o que falta e
   * POR QUÊ. Só entra aqui o que realmente impede o conteúdo de funcionar — o resto vira
   * aviso, não bloqueio.
   */
  const problemas = [];
  if (!f.title.trim())
    problemas.push("O título — é ele que aparece no card, na busca e no player.");
  if (!f.contentUrl.trim())
    problemas.push(
      ehVideo
        ? "O vídeo — envie o arquivo ou cole a URL. Sem ele não há o que reproduzir."
        : "A URL do documento — sem ela não há o que abrir."
    );
  if (!f.thumbnailUrl.trim())
    problemas.push("A capa — sem ela o conteúdo aparece como um card vazio no app.");
  if (!f.templateId)
    problemas.push("O template — é a página de venda que aparece para quem não assina.");

  const ocupado = salvando || video.enviando || capa.enviando;
  const podeSalvar = problemas.length === 0 && !ocupado;

  return (
    <div className="adm__modalFundo" onClick={ocupado ? undefined : onFechar}>
      <div
        className="adm__modal"
        onClick={(e) => e.stopPropagation()}
        style={{ width: "min(720px, 100%)" }}
      >
        <h3>{editando ? "Editar conteúdo" : "Novo conteúdo"}</h3>
        <p className="adm__modalHint">
          O conteúdo é a peça do acervo: um vídeo (ou documento) com sua capa e as regras de
          quem pode ver. As séries e as seções da home apenas <strong>apontam</strong> para ele.
        </p>

        <Erro erro={erro} onFechar={onLimparErro} />

        <div className="adm__campo">
          <label>Título</label>
          <input
            className="adm__input"
            value={f.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="ex.: A Criação do Mundo"
            autoFocus
          />
        </div>

        <div className="adm__campo">
          <label>Subtítulo</label>
          <input
            className="adm__input"
            value={f.subtitle}
            onChange={(e) => set("subtitle", e.target.value)}
            placeholder="ex.: Gênesis 1"
          />
        </div>

        <div className="adm__campo">
          <label>Descrição</label>
          <textarea
            className="adm__input"
            rows={2}
            value={f.description}
            onChange={(e) => set("description", e.target.value)}
            style={{ resize: "vertical" }}
          />
        </div>

        <div className="adm__campo">
          <label>Tipo do conteúdo</label>
          <select
            className="adm__select"
            value={f.contentType}
            onChange={(e) => set("contentType", e.target.value)}
          >
            <option value="video">Vídeo</option>
            <option value="document">Documento</option>
          </select>
        </div>

        {/* ---------- o vídeo ---------- */}
        {ehVideo && (
          <div className="adm__campo">
            <label>Vídeo</label>
            <CaixaUpload>
              <button
                className={video.ok ? "adm__btnGhost" : "adm__btn"}
                onClick={() => inputVideo.current?.click()}
                disabled={video.enviando}
                style={{ width: "100%", justifyContent: "center" }}
              >
                {video.enviando
                  ? `Enviando vídeo… ${video.progresso}%`
                  : video.ok
                    ? "✓ Vídeo enviado — enviar outro"
                    : "Enviar vídeo (Bunny Stream)"}
              </button>
              <input
                ref={inputVideo}
                type="file"
                accept="video/*"
                hidden
                onChange={aoEscolherVideo}
              />

              {video.enviando && (
                <>
                  <Progresso valor={video.progresso} />
                  <p style={{ fontSize: 11.5, color: "var(--text3)", margin: "7px 0 0" }}>
                    O arquivo é grande — não feche esta janela até chegar a 100%.
                  </p>
                </>
              )}

              {video.erro && (
                <p style={{ fontSize: 12, color: "var(--cancel)", margin: "8px 0 0" }}>
                  {video.erro}
                </p>
              )}

              {video.ok && !video.enviando && (
                <p style={{ fontSize: 11.5, color: "var(--green)", margin: "8px 0 0" }}>
                  Enviado — já pode salvar. O Bunny ainda transcodifica por alguns minutos; até
                  lá o vídeo pode não abrir no app.
                </p>
              )}
            </CaixaUpload>
          </div>
        )}

        <div className="adm__campo">
          <label>{ehVideo ? "URL do vídeo" : "URL do documento"}</label>
          <input
            className="adm__input"
            value={f.contentUrl}
            onChange={(e) => set("contentUrl", e.target.value)}
            placeholder="https://…"
          />
          <p style={{ fontSize: 11, color: "var(--text3)", margin: "6px 0 0" }}>
            {ehVideo
              ? "O upload acima preenche este campo sozinho. Colar a URL na mão também vale — YouTube, Bunny ou qualquer embed."
              : "Cole o endereço do documento. O upload de arquivo aqui é só para vídeo (Bunny Stream)."}
          </p>
        </div>

        {/* ---------- a capa ---------- */}
        <div className="adm__campo">
          <label>Capa</label>
          <CaixaUpload>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              {/* Preview: a capa é o que vende o conteúdo no app. Ver antes de salvar evita
                  descobrir a imagem errada só depois de publicada. */}
              <div
                style={{
                  width: 84,
                  height: 118,
                  flex: "0 0 84px",
                  borderRadius: 8,
                  border: "1px solid var(--border)",
                  background: "var(--card)",
                  backgroundImage: f.thumbnailUrl ? `url(${f.thumbnailUrl})` : undefined,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  display: "grid",
                  placeItems: "center",
                  fontSize: 10.5,
                  color: "var(--text3)",
                  textAlign: "center",
                  overflow: "hidden",
                }}
              >
                {!f.thumbnailUrl && "sem capa"}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <button
                  className="adm__btnGhost"
                  onClick={() => inputCapa.current?.click()}
                  disabled={capa.enviando}
                >
                  {capa.enviando
                    ? `Enviando capa… ${capa.progresso}%`
                    : f.thumbnailUrl
                      ? "Trocar capa"
                      : "Enviar capa (imagem)"}
                </button>
                <input
                  ref={inputCapa}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={aoEscolherCapa}
                />

                {capa.enviando && <Progresso valor={capa.progresso} />}

                {capa.erro && (
                  <p style={{ fontSize: 12, color: "var(--cancel)", margin: "8px 0 0" }}>
                    {capa.erro}
                  </p>
                )}

                {capaVeioDoVideo && (
                  <p style={{ fontSize: 11.5, color: "var(--text2)", margin: "8px 0 0" }}>
                    Esta capa é um <strong>frame do vídeo</strong>, gerado pelo Bunny. Serve, mas
                    uma imagem escolhida vende melhor.
                  </p>
                )}

                <input
                  className="adm__input"
                  value={f.thumbnailUrl}
                  onChange={(e) => {
                    set("thumbnailUrl", e.target.value);
                    setCapaVeioDoVideo(false);
                  }}
                  placeholder="https://…"
                  style={{ marginTop: 9 }}
                />
                <p style={{ fontSize: 11, color: "var(--text3)", margin: "6px 0 0" }}>
                  Ou cole a URL da imagem direto.
                </p>
              </div>
            </div>
          </CaixaUpload>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <div className="adm__campo">
            <label>Acesso</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
              {tagsAcesso.map((t) => {
                const on = f.access.includes(t);
                return (
                  <button
                    key={t}
                    onClick={() => alternarAcesso(t)}
                    className={`adm__chip ${on ? "adm__chip--red" : ""}`}
                    style={{ cursor: "pointer", border: on ? "1px solid var(--red)" : undefined }}
                  >
                    {t}
                  </button>
                );
              })}
            </div>

            <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
              <input
                className="adm__input"
                value={novaTag}
                onChange={(e) => setNovaTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    adicionarTag();
                  }
                }}
                placeholder="outra tag…"
                style={{ padding: "6px 10px", fontSize: 12 }}
              />
              <button className="adm__btnGhost" onClick={adicionarTag} disabled={!novaTag.trim()}>
                Adicionar
              </button>
            </div>

            {/* Sem tag ninguém abre o conteúdo: `isBlocked` só é falso se alguma tag bater com
                a assinatura do usuário. Não bloqueia o salvamento — pode ser proposital
                (conteúdo em preparação) — mas a pessoa precisa saber. */}
            {!f.access.length && (
              <p style={{ fontSize: 11.5, color: "var(--orange)", margin: "8px 0 0" }}>
                Sem nenhuma tag, o conteúdo fica bloqueado para todo mundo — inclusive para quem
                assina.
              </p>
            )}
          </div>

          <div className="adm__campo">
            <label>Template de venda</label>
            <select
              className="adm__select"
              value={f.templateId}
              onChange={(e) => set("templateId", e.target.value)}
              disabled={templates.length === 0}
            >
              <option value="">Selecione…</option>
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>
                  {t.template_name}
                </option>
              ))}
            </select>
            <p style={{ fontSize: 11, color: "var(--text3)", margin: "6px 0 0" }}>
              {templates.length === 0
                ? "Carregando templates…"
                : "A página que aparece para quem não assinou."}
            </p>
          </div>
        </div>

        <div className="adm__campo">
          <label>Informação adicional (HTML)</label>
          <textarea
            className="adm__input"
            rows={3}
            value={f.additionalInfo}
            onChange={(e) => set("additionalInfo", e.target.value)}
            placeholder="<p>Conteúdo rico que aparece embaixo do vídeo…</p>"
            style={{ resize: "vertical", fontFamily: '"Roboto Mono", monospace', fontSize: 12.5 }}
          />
        </div>

        {problemas.length > 0 && (
          <div
            style={{
              border: "1px solid var(--border)",
              borderLeft: "3px solid var(--orange)",
              borderRadius: 9,
              padding: "11px 14px",
              marginTop: 16,
              background: "var(--elev)",
            }}
          >
            <strong style={{ fontSize: 12.5 }}>Falta preencher para salvar:</strong>
            <ul style={{ margin: "7px 0 0", paddingLeft: 18, fontSize: 12.5, lineHeight: 1.7 }}>
              {problemas.map((p) => (
                <li key={p} style={{ color: "var(--text2)" }}>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}

        {confirmandoExclusao ? (
          /* Exclusão é destrutiva e em CASCATA. O texto diz exatamente o que vai acontecer —
             "Tem certeza?" sozinho não informa nada e a pessoa clica em Sim no automático. */
          <div
            style={{
              border: "1px solid rgba(255,90,95,.35)",
              borderLeft: "3px solid var(--cancel)",
              borderRadius: 9,
              padding: "13px 15px",
              marginTop: 16,
              background: "var(--elev)",
            }}
          >
            <strong style={{ fontSize: 13.5 }}>Excluir “{f.titulo || "este conteúdo"}”?</strong>
            <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "var(--text2)", lineHeight: 1.6 }}>
              Ele sai do acervo <strong>e também de qualquer série ou faixa da home onde
              apareça</strong> — a remoção é em cascata. As séries e a home continuam
              funcionando; o item só some delas.
              <br />
              <strong style={{ color: "var(--text)" }}>Não dá para desfazer pelo painel.</strong>
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 13, justifyContent: "flex-end" }}>
              <button
                className="adm__btnGhost"
                onClick={() => setConfirmandoExclusao(false)}
                disabled={ocupado}
              >
                Não, manter
              </button>
              <button
                className="adm__btn adm__btnDanger"
                onClick={() => onExcluir(f)}
                disabled={ocupado}
              >
                {salvando ? "Excluindo…" : "Sim, excluir"}
              </button>
            </div>
          </div>
        ) : (
          <div className="adm__modalAcoes">
            {editando && (
              <button
                className="adm__btnGhost"
                onClick={() => setConfirmandoExclusao(true)}
                disabled={ocupado}
                style={{ marginRight: "auto", color: "var(--cancel)", borderColor: "rgba(255,90,95,.3)" }}
              >
                Excluir
              </button>
            )}
            <button className="adm__btnGhost" onClick={onFechar} disabled={ocupado}>
              Cancelar
            </button>
            <button className="adm__btn" disabled={!podeSalvar} onClick={() => onSalvar(f)}>
              {salvando ? "Salvando…" : editando ? "Salvar conteúdo" : "Criar conteúdo"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
